import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  Download,
  Upload,
  UserPlus,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { ApiResponse } from "../types/common";
import { ApiError, usersApi, organizationsApi } from "../utils/api";

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  status: "active" | "inactive" | "pending";
  joinDate: string;
  location: string;
  organization: string;
  avatar?: string;
  verified: boolean;
  lastActive: string;
  stats?: {
    sessions?: number;
    earnings?: number;
    rating?: number;
    reviews?: number;
  };
}

const mapApiUserToUser = (apiUser: any): User => {
  const profile = apiUser.profile || {};
  const firstName = profile.firstName || "";
  const lastName = profile.lastName || "";
  const name = `${firstName} ${lastName}`.trim() || "Unknown";
  const email = apiUser.emails?.[0]?.address || "";
  const phone = apiUser.username || "";
  const role = apiUser.roles?.[0] || profile.accountType || "UNKNOWN";
  const status = apiUser.disabled ? "inactive" : "active";
  const verified = profile.verified || false;

  return {
    id: apiUser._id,
    name,
    email,
    phone,
    role,
    status,
    joinDate: apiUser.createdAt || "",
    location: profile.address || "",
    organization: apiUser.organizationName || "",
    avatar: profile.avatar,
    verified,
    lastActive: apiUser.updatedAt || "",
  };
};

interface OrganizationCard {
  id: string;
  organizationName: string;
  organizationType?: string;
  city?: string;
  state?: string;
  email?: string;
  phone?: string;
  disabled: boolean;
  totalLicensesUsed: number;
  totalLicensesPurchased: number;
  planName?: string;
  createdAt: string;
}

const mapApiOrgToCard = (apiOrg: any): OrganizationCard => ({
  id: apiOrg._id,
  organizationName: apiOrg.organizationName || "Unnamed Organization",
  organizationType: apiOrg.organizationType,
  city: apiOrg.city,
  state: apiOrg.state,
  email: apiOrg.email,
  phone: apiOrg.phone,
  disabled: !!apiOrg.disabled,
  totalLicensesUsed: apiOrg.totalLicensesUsed || 0,
  totalLicensesPurchased: apiOrg.totalLicensesPurchased || 0,
  planName: apiOrg.planName,
  createdAt: apiOrg.createdAt || "",
});

interface UsersProps {
  searchTerm: string;
  filterRole: string;
}

export function Users({ searchTerm, filterRole }: UsersProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationCard[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    location: "",
    role: "",
    status: "active" as User["status"],
  });
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mock data
  useEffect(() => {
    // const mockUsers: User[] = [
    //   {
    //     id: "1",
    //     name: "Sarah Johnson",
    //     email: "sarah.johnson@email.com",
    //     phone: "+1 (555) 123-4567",
    //     role: "COACH",
    //     status: "active",
    //     joinDate: "2024-01-15",
    //     location: "New York, NY",
    //     avatar:
    //       "https://images.unsplash.com/photo-1494790108755-2616b612d5c5?w=100&h=100&fit=crop&crop=face",
    //     verified: true,
    //     lastActive: "2 hours ago",
    //     stats: {
    //       sessions: 127,
    //       earnings: 12450,
    //       rating: 4.9,
    //       reviews: 89,
    //     },
    //   },
    //   {
    //     id: "2",
    //     name: "Mike Chen",
    //     email: "mike.chen@email.com",
    //     phone: "+1 (555) 234-5678",
    //     role: "ATHLETE",
    //     status: "active",
    //     joinDate: "2024-02-20",
    //     location: "Los Angeles, CA",
    //     avatar:
    //       "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    //     verified: true,
    //     lastActive: "1 day ago",
    //     stats: {
    //       sessions: 45,
    //       rating: 4.7,
    //       reviews: 23,
    //     },
    //   },
    //   {
    //     id: "3",
    //     name: "Emma Davis",
    //     email: "emma.davis@email.com",
    //     phone: "+1 (555) 345-6789",
    //     role: "AGENT",
    //     status: "pending",
    //     joinDate: "2024-03-10",
    //     location: "Chicago, IL",
    //     avatar:
    //       "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
    //     verified: false,
    //     lastActive: "5 days ago",
    //     stats: {
    //       sessions: 12,
    //       earnings: 2100,
    //       rating: 4.2,
    //       reviews: 8,
    //     },
    //   },
    //   {
    //     id: "4",
    //     name: "Alex Thompson",
    //     email: "alex.thompson@email.com",
    //     phone: "+1 (555) 456-7890",
    //     role: "COACH",
    //     status: "inactive",
    //     joinDate: "2023-11-05",
    //     location: "Austin, TX",
    //     avatar:
    //       "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
    //     verified: true,
    //     lastActive: "2 weeks ago",
    //     stats: {
    //       sessions: 203,
    //       earnings: 18900,
    //       rating: 4.8,
    //       reviews: 156,
    //     },
    //   },
    //   {
    //     id: "5",
    //     name: "Lisa Rodriguez",
    //     email: "lisa.rodriguez@email.com",
    //     phone: "+1 (555) 567-8901",
    //     role: "ATHLETE",
    //     status: "active",
    //     joinDate: "2024-01-28",
    //     location: "Miami, FL",
    //     avatar:
    //       "https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?w=100&h=100&fit=crop&crop=face",
    //     verified: true,
    //     lastActive: "30 minutes ago",
    //     stats: {
    //       sessions: 67,
    //       rating: 4.6,
    //       reviews: 34,
    //     },
    //   },
    // ];

    loadUsers();

    setTimeout(() => {
      
      setIsLoading(false);
    }, 1000);
  }, []);

  // Load stats
  const loadUsers = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        page,
        limit: 10,
      };

      const response: ApiResponse = await usersApi.getUsers(params);

      if (response.success) {
        const rawUsers = response.data.users || response.data || [];
        const mappedUsers = rawUsers.map(mapApiUserToUser);
        setUsers(mappedUsers);
      } else {
        throw new Error(response.message || "Failed to load users");
      }
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to load users";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loadOrganizations = async (search: string) => {
    try {
      setIsLoadingOrgs(true);
      setOrgError(null);

      const response: ApiResponse = await organizationsApi.getAll({
        limit: 100,
        search: search || undefined,
      });

      if (response.success) {
        const rawOrgs = response.data.organizations || [];
        setOrganizations(rawOrgs.map(mapApiOrgToCard));
      } else {
        throw new Error(response.message || "Failed to load organizations");
      }
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to load organizations";
      setOrgError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  useEffect(() => {
    if (filterRole === "ORGANIZATION") {
      loadOrganizations(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole, searchTerm]);

  const isOrgView = filterRole === "ORGANIZATION";

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      (user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone || "").includes(searchTerm) ||
      (user.organization || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = filterRole === "all" || user.role === filterRole;
    const matchesStatus =
      statusFilter === "all" || user.status === statusFilter;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    switch (sortBy) {
      case "name":
        return (a.name || "").localeCompare(b.name || "");
      case "joinDate":
        return new Date(b.joinDate || 0).getTime() - new Date(a.joinDate || 0).getTime();
      case "role":
        return (a.role || "").localeCompare(b.role || "");
      case "status":
        return (a.status || "").localeCompare(b.status || "");
      default:
        return 0;
    }
  });

  const escapeCsvValue = (value: string) => {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleExportUsers = () => {
    if (sortedUsers.length === 0) {
      toast.info("No users to export");
      return;
    }

    const headers = [
      "Name",
      "Email",
      "Phone",
      "Role",
      "Status",
      "Location",
      "Joined",
      "Last Active",
    ];
    const rows = sortedUsers.map((user) => [
      user.name,
      user.email,
      user.phone,
      user.role,
      user.status,
      user.location,
      user.joinDate,
      user.lastActive,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => escapeCsvValue(String(cell ?? ""))).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${sortedUsers.length} user${sortedUsers.length === 1 ? "" : "s"}`);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setIsImporting(true);
    try {
      const response: ApiResponse = await usersApi.importUsers(file);

      if (response.success) {
        const { created = 0, updated = 0, failed = [] } = response.data || {};
        toast.success(
          `Import complete: ${created} created, ${updated} updated${
            failed.length ? `, ${failed.length} failed` : ""
          }`
        );
        if (failed.length) {
          console.warn("Import row failures:", failed);
        }
        await loadUsers();
      } else {
        throw new Error(response.message || "Failed to import users");
      }
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? `${err.message} (${err.status})`
          : "Failed to import users";
      toast.error(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "COACH":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "ATHLETE":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "AGENT":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "inactive":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const handleUserAction = (action: string, userId: string) => {
    switch (action) {
      case "delete":
        toast.error(`Deleting user ${userId}`);
        break;
      case "activate":
        toast.success(`User ${userId} activated`);
        break;
      case "deactivate":
        toast.warning(`User ${userId} deactivated`);
        break;
      default:
        break;
    }
  };

  const openEditUser = (user: User) => {
    const [firstName = "", ...rest] = (user.name || "").split(" ");
    setEditingUser(user);
    setEditForm({
      firstName,
      lastName: rest.join(" "),
      email: user.email || "",
      phone: user.phone || "",
      location: user.location || "",
      role: user.role || "",
      status: user.status,
    });
  };

  const handleSaveEditUser = async () => {
    if (!editingUser) return;

    if (!editForm.firstName.trim()) {
      toast.error("First name is required");
      return;
    }

    setIsSavingUser(true);
    try {
      // Backend does a raw $set of the body, so nested fields are sent as
      // dot-notation paths — that patches just those fields instead of
      // overwriting the whole profile/emails subdocument and losing
      // sibling data (verified flags, onboarding state, etc).
      const payload: Record<string, any> = {
        "profile.firstName": editForm.firstName.trim(),
        "profile.lastName": editForm.lastName.trim(),
        "profile.address": editForm.location.trim(),
        "emails.0.address": editForm.email.trim(),
        username: editForm.phone.trim(),
        // profile.accountType has a strict ATHLETE|COACH|AGENT|ADMIN enum,
        // but roles (unrestricted) is what actually drives roles like
        // ORGANIZATION in the user list — update roles always, accountType
        // only when the value is valid for it.
        roles: [editForm.role],
        disabled: editForm.status !== "active",
        "profile.isAccountDisabled": editForm.status !== "active",
      };

      if (["ATHLETE", "COACH", "AGENT", "ADMIN"].includes(editForm.role)) {
        payload["profile.accountType"] = editForm.role;
      }

      const response = await usersApi.updateUser(editingUser.id, payload);

      if (response.success === false) {
        throw new Error(response.message || "Failed to update user");
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingUser.id
            ? {
                ...u,
                name: `${editForm.firstName} ${editForm.lastName}`.trim(),
                email: editForm.email,
                phone: editForm.phone,
                location: editForm.location,
                role: editForm.role,
                status: editForm.status,
              }
            : u
        )
      );

      toast.success("User updated");
      setEditingUser(null);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Failed to update user"
      );
    } finally {
      setIsSavingUser(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>User Management</h1>
            <p>Manage coaches, athletes, and agents on the platform.</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-20"></div>
                  <div className="h-8 bg-muted rounded w-16"></div>
                  <div className="h-3 bg-muted rounded w-24"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: "Total Users",
      value: users.length.toString(),
      description: "All registered users",
      icon: UserPlus,
    },
    {
      title: "Active Users",
      value: users.filter((u) => u.status === "active").length.toString(),
      description: "Currently active",
      icon: CheckCircle,
    },
    {
      title: "Coaches",
      value: users
        .filter((u) => u.role === "COACH" && u.status !== "inactive")
        .length.toString(),
      description: "Verified coaches",
      icon: Shield,
    },
    {
      title: "Athletes",
      value: users
        .filter((u) => u.role === "ATHLETE" && u.status !== "inactive")
        .length.toString(),
      description: "Registered athletes",
      icon: UserPlus,
    },
    {
      title: "Organizations",
      value: users
        .filter((u) => u.role === "ORGANIZATION" && u.status !== "inactive")
        .length.toString(),
      description: "Registered organizations",
      icon: Building2,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>User Management</h1>
          <p>Manage coaches, athletes, and agents on the platform.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            className="hidden"
            onChange={handleImportFileChange}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleImportClick}
            disabled={isImporting}
          >
            <Upload className="h-4 w-4 mr-2" />
            {isImporting ? "Importing..." : "Import"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportUsers}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button size="sm" onClick={() => toast.info("Add User feature coming soon")}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {stats.map((stat, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between">
        {isOrgView ? <div /> : (
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="joinDate">Join Date</SelectItem>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "table" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("table")}
          >
            Table
          </Button>
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            Grid
          </Button>
        </div>
      </div>

      {isOrgView && orgError && (
        <p className="text-sm text-destructive">{orgError}</p>
      )}

      {isOrgView && isLoadingOrgs && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-32"></div>
                  <div className="h-3 bg-muted rounded w-24"></div>
                  <div className="h-3 bg-muted rounded w-40"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isOrgView && !isLoadingOrgs && organizations.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            No organizations found.
          </CardContent>
        </Card>
      )}

      {isOrgView && !isLoadingOrgs && organizations.length > 0 && viewMode === "table" && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Licenses</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">{org.organizationName}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {org.organizationType ? (
                      <Badge variant="secondary">{org.organizationType}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {org.city || org.state ? (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {[org.city, org.state].filter(Boolean).join(", ")}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      {org.email && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[160px]">{org.email}</span>
                        </div>
                      )}
                      {org.phone && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {org.phone}
                        </div>
                      )}
                      {!org.email && !org.phone && (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {org.totalLicensesUsed}/{org.totalLicensesPurchased}
                    </span>
                  </TableCell>
                  <TableCell>
                    {org.disabled ? (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        Disabled
                      </Badge>
                    ) : (
                      <Badge className="gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        <CheckCircle className="h-3 w-3" />
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {isOrgView && !isLoadingOrgs && organizations.length > 0 && viewMode === "grid" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {organizations.map((org) => (
                <Card key={org.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base">
                            {org.organizationName}
                          </CardTitle>
                          {org.organizationType && (
                            <Badge variant="secondary" className="mt-1">
                              {org.organizationType}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {org.disabled ? (
                        <Badge variant="destructive" className="gap-1">
                          <XCircle className="h-3 w-3" />
                          Disabled
                        </Badge>
                      ) : (
                        <Badge className="gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                          <CheckCircle className="h-3 w-3" />
                          Active
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {(org.city || org.state) && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        {[org.city, org.state].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {org.email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        {org.email}
                      </div>
                    )}
                    {org.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        {org.phone}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm text-muted-foreground">
                        {org.totalLicensesUsed}/{org.totalLicensesPurchased} licenses
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {org.createdAt
                          ? new Date(org.createdAt).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
      )}

      {!isOrgView && (
      <>
      {viewMode === "table" ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback>
                          {(user.name || "")
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{user.name}</span>
                          {user.verified && (
                            <CheckCircle className="h-4 w-4 text-blue-500" />
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {user.phone}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getRoleColor(user.role)}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(user.status)}
                      <span className="capitalize">{user.status}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {user.location}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {new Date(user.joinDate).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : "N/A"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem
                          onSelect={() => setSelectedUser(user)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openEditUser(user)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            handleUserAction(
                              user.status === "active"
                                ? "deactivate"
                                : "activate",
                              user.id
                            )
                          }
                        >
                          {user.status === "active" ? (
                            <>
                              <XCircle className="h-4 w-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUserAction("delete", user.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedUsers.map((user) => (
            <Card key={user.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={user.avatar} alt={user.name} />
                      <AvatarFallback>
                        {(user.name || "")
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{user.name}</CardTitle>
                        {user.verified && (
                          <CheckCircle className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <Badge className={getRoleColor(user.role)}>
                        {user.role}
                      </Badge>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setSelectedUser(user)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => openEditUser(user)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit User
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    {user.email}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    {user.phone}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {user.location}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(user.status)}
                    <span className="text-sm capitalize">{user.status}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : "N/A"}
                  </span>
                </div>

                {user.stats && (
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                    <div className="text-center">
                      <div className="text-lg font-semibold">
                        {user.stats.sessions || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Sessions
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold">
                        {user.stats.rating ? `${user.stats.rating}★` : "N/A"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Rating
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </>
      )}

      {/* User Details Dialog */}
      {selectedUser && (
        <Dialog
          open={!!selectedUser}
          onOpenChange={() => setSelectedUser(null)}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
              <DialogDescription>
                Complete information for {selectedUser.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage
                      src={selectedUser.avatar}
                      alt={selectedUser.name}
                    />
                    <AvatarFallback className="text-lg">
                      {(selectedUser.name || "")
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-semibold">
                        {selectedUser.name}
                      </h3>
                      {selectedUser.verified && (
                        <CheckCircle className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    <Badge className={getRoleColor(selectedUser.role)}>
                      {selectedUser.role}
                    </Badge>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {getStatusIcon(selectedUser.status)}
                      <span className="capitalize">{selectedUser.status}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      {selectedUser.email}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      {selectedUser.phone}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      {selectedUser.location}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Join Date</Label>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {new Date(selectedUser.joinDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit User Dialog */}
      {editingUser && (
        <Dialog
          open={!!editingUser}
          onOpenChange={(open) => !open && setEditingUser(null)}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update details for {editingUser.name}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="edit-first-name">First Name</Label>
                <Input
                  id="edit-first-name"
                  value={editForm.firstName}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, firstName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last-name">Last Name</Label>
                <Input
                  id="edit-last-name"
                  value={editForm.lastName}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, lastName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, phone: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-location">Location</Label>
                <Input
                  id="edit-location"
                  value={editForm.location}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, location: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(value) =>
                    setEditForm((f) => ({ ...f, role: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COACH">Coach</SelectItem>
                    <SelectItem value="ATHLETE">Athlete</SelectItem>
                    <SelectItem value="AGENT">Agent</SelectItem>
                    <SelectItem value="ORGANIZATION">Organization</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) =>
                    setEditForm((f) => ({
                      ...f,
                      status: value as User["status"],
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditingUser(null)}
                disabled={isSavingUser}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveEditUser} disabled={isSavingUser}>
                {isSavingUser ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
